import { useEffect, useState } from 'react';

const MOBILE_BREAKPOINT = 768;
const TABLET_BREAKPOINT = 1280;

// Synchronous viewport read used to seed the initial state. We're a client-only
// Vite SPA so `window` is always defined at render time; the typeof guard is
// belt-and-braces for the rare test that loads this module before jsdom is
// ready. Seeding from `window.innerWidth` (rather than the `useState(undefined)`
// + effect dance the original used) means a real mobile viewport renders the
// "best on desktop" interstitial on first paint — without it, the Welcome hero
// flashes briefly before the warning mounts (NEA-5932 follow-up).
function readIsMobile() {
	if (typeof window === 'undefined') return false;
	return window.innerWidth < MOBILE_BREAKPOINT;
}

function readIsTablet() {
	if (typeof window === 'undefined') return false;
	return window.innerWidth < TABLET_BREAKPOINT;
}

/** True when the viewport is narrower than the mobile breakpoint (<768px). */
export function useIsMobile() {
	const [isMobile, setIsMobile] = useState(readIsMobile);

	useEffect(() => {
		const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
		const onChange = () => setIsMobile(readIsMobile());
		mql.addEventListener('change', onChange);
		// Re-sync once on mount in case the viewport changed between module load
		// and component mount (e.g. orientation change during route transition).
		setIsMobile(readIsMobile());
		return () => mql.removeEventListener('change', onChange);
	}, []);

	return !!isMobile;
}

/**
 * True when the viewport is narrower than the tablet/small-desktop breakpoint
 * (<1280px = below Tailwind `xl`). Drives the auto-collapse of the left
 * sidebar so the main content gets the full viewport on iPads and small
 * laptops (NEA-6038).
 */
export function useIsTablet() {
	const [isTablet, setIsTablet] = useState(readIsTablet);

	useEffect(() => {
		const mql = window.matchMedia(`(max-width: ${TABLET_BREAKPOINT - 1}px)`);
		const onChange = () => setIsTablet(readIsTablet());
		mql.addEventListener('change', onChange);
		setIsTablet(readIsTablet());
		return () => mql.removeEventListener('change', onChange);
	}, []);

	return !!isTablet;
}
